﻿import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useIsFetching } from "@tanstack/react-query";
import { Sidebar } from "../components/layout/Sidebar";
import { Topbar } from "../components/layout/Topbar";
import { DashboardPage } from "../pages/DashboardPage";
import { ObjectsPage } from "../pages/ObjectsPage";
import { ObjectDetailPage } from "../pages/ObjectDetailPage";
import { UnitsPage } from "../pages/UnitsPage";
import { OwnersPage } from "../pages/OwnersPage";
import { AssignmentsPage } from "../pages/AssignmentsPage";
import { ImportsPage } from "../pages/ImportsPage";
import { ResolutionsPage } from "../pages/ResolutionsPage";
import { ResolutionDetailPage } from "../pages/ResolutionDetailPage";
import { AnalogScanPage } from "../pages/AnalogScanPage";
import { AuditPage } from "../pages/AuditPage";
import { LogsPage } from "../pages/LogsPage";
import { SettingsPage } from "../pages/SettingsPage";
import { AdminUsersPage } from "../pages/AdminUsersPage";
import { PlatformTenantsPage } from "../pages/PlatformTenantsPage";
import { TemplatesPage } from "../pages/TemplatesPage";
import { OwnerVotingLoginPage } from "../pages/OwnerVotingLoginPage";
import { OwnerVotingPage } from "../pages/OwnerVotingPage";
import { AdminLoginPage } from "../pages/AdminLoginPage";
import { LandingPage } from "../pages/LandingPage";
import { ImpressumPage } from "../pages/ImpressumPage";
import { DatenschutzPage } from "../pages/DatenschutzPage";
import { GlobalSearchPage } from "../pages/GlobalSearchPage";
import { api, type TenantTrialRuntime, type UiSettingsApi } from "./api";
import { clearAuthToken, getAuthToken, type AuthUser } from "./auth";
import { resolveHostSlugFromHostname, resolveTenantSlugFromHostname } from "./tenant-host";
import { defaultUiSettings, loadUiSettings, saveUiSettings, type UiSettings } from "./ui-settings";
import { applyUiFrameworkMode } from "./ui-framework";
import backendCheckBg from "../assets/backend-check-bg.png";

const persistedSettingsFingerprint = (value: UiSettings) => {
  const { settingsUpdatedAt: _settingsUpdatedAt, ...settingsWithoutRevision } = value;
  return JSON.stringify(settingsWithoutRevision);
};

export const App = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<UiSettings>(() => loadUiSettings());
  const [settingsHydrated, setSettingsHydrated] = useState(false);
  const [settingsSaveStatus, setSettingsSaveStatus] = useState<{ type: "ok" | "info"; text: string } | null>(null);
  const lastPersistedSettingsRef = useRef<string | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [authServerError, setAuthServerError] = useState<string | null>(null);
  const [authRetryNonce, setAuthRetryNonce] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [faciliooSyncBadge, setFaciliooSyncBadge] = useState<{ tone: "ok" | "error"; label: string } | null>(null);
  const [tenantTrialStatus, setTenantTrialStatus] = useState<TenantTrialRuntime | null>(null);
  const [tenantSlug, setTenantSlug] = useState("default");
  const [hostSlug, setHostSlug] = useState("default");
  const isFetchingCount = useIsFetching({
    predicate: (query) => query.isActive()
  });
  const [globalFetchLoaderVisible, setGlobalFetchLoaderVisible] = useState(false);

  const vitePlatformAliases =
    (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_PLATFORM_TENANT_ALIASES ?? "plattform,platform";
  const platformTenantAliases = new Set(
    String(vitePlatformAliases)
      .split(",")
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean)
  );

  useEffect(() => {
    saveUiSettings(settings);
  }, [settings]);

  useEffect(() => {
    const handleUiSettingsUpdated = () => {
      setSettings(loadUiSettings());
    };
    window.addEventListener("ui-settings-updated", handleUiSettingsUpdated);
    return () => {
      window.removeEventListener("ui-settings-updated", handleUiSettingsUpdated);
    };
  }, []);

  useEffect(() => {
    const host = window.location.hostname.toLowerCase();
    setTenantSlug(resolveTenantSlugFromHostname(host));
    setHostSlug(resolveHostSlugFromHostname(host));
  }, []);

  useEffect(() => {
    applyUiFrameworkMode(settings.management.uiFramework === "mockup_v2" ? "mockup_v2" : "classic");
  }, [settings.management.uiFramework]);

  useEffect(() => {
    let showTimer: number | null = null;
    let hideTimer: number | null = null;
    if (isFetchingCount > 0) {
      showTimer = window.setTimeout(() => {
        setGlobalFetchLoaderVisible(true);
      }, 120);
    } else {
      hideTimer = window.setTimeout(() => {
        setGlobalFetchLoaderVisible(false);
      }, 80);
    }
    return () => {
      if (showTimer) window.clearTimeout(showTimer);
      if (hideTimer) window.clearTimeout(hideTimer);
    };
  }, [isFetchingCount]);

  const platformPortalAliasesRaw =
    (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_PLATFORM_PORTAL_ALIASES ?? "plattform,platform";
  const platformPortalAliases = new Set(
    String(platformPortalAliasesRaw)
      .split(",")
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean)
  );
  const landingHostsRaw =
    (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_PUBLIC_LANDING_HOSTS ??
    "www.derumlaufbeschluss.de,localhost,127.0.0.1";
  const landingHosts = new Set(
    String(landingHostsRaw)
      .split(",")
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean)
  );
  const currentHost = window.location.hostname.toLowerCase();
  const isLandingHost = landingHosts.has(currentHost);
  const isPlatformPortalHost = platformPortalAliases.has(hostSlug);
  const isOwnerArea = location.pathname.startsWith("/owner-login") || location.pathname.startsWith("/owner-vote/");

  useEffect(() => {
    if (!authUser || isOwnerArea) return;
    if (isPlatformPortalHost) {
      document.title = "ObjektVote | Plattform";
      return;
    }
    const tenantLabel = String(settings.management.firmenname || tenantSlug || "Mandant").trim();
    document.title = `ObjektVote | ${tenantLabel}`;
  }, [authUser, isOwnerArea, isPlatformPortalHost, settings.management.firmenname, tenantSlug]);

  useEffect(() => {
    let active = true;
    const token = getAuthToken();
    if (!token || isOwnerArea) {
      setSettingsHydrated(false);
      return;
    }

    setSettingsHydrated(false);
    api.uiSettings()
      .then((remote) => {
        if (!active) return;

        const next: UiSettings = {
          ...defaultUiSettings,
          ...remote,
          management: { ...defaultUiSettings.management, ...(remote.management ?? {}) },
          mail: { ...defaultUiSettings.mail, ...(remote.mail ?? {}) },
          reminders: { ...defaultUiSettings.reminders, ...(remote.reminders ?? {}) },
          theme: { ...defaultUiSettings.theme, ...(remote.theme ?? {}) },
          ownerTheme: { ...defaultUiSettings.ownerTheme, ...(remote.ownerTheme ?? {}) }
        };
        lastPersistedSettingsRef.current = persistedSettingsFingerprint(next);
        setSettings(next);
        saveUiSettings(next);
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setSettingsHydrated(true);
      });

    return () => {
      active = false;
    };
  }, [authUser?.id, isOwnerArea, tenantSlug]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let active = true;
    let timer: number | null = null;

    const loadSyncBadge = async () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      if (settings.management.faciliooEnabled === false || !settings.management.faciliooLiveSyncEnabled) {
        if (active) setFaciliooSyncBadge(null);
        return;
      }
      try {
        const status = await api.getFaciliooSyncStatus();
        if (!active) return;
        if (!status.enabled) {
          setFaciliooSyncBadge(null);
          return;
        }
        if (status.running) {
          setFaciliooSyncBadge({ tone: "ok", label: "live" });
          return;
        }
        const hasError = status.lastRun?.ok === false;
        setFaciliooSyncBadge({
          tone: hasError ? "error" : "ok",
          label: hasError ? "Live-Sync Fehler" : "live"
        });
      } catch {
        if (!active) return;
        setFaciliooSyncBadge({ tone: "error", label: "Live-Sync Fehler" });
      }
    };

    void loadSyncBadge();
    timer = window.setInterval(() => {
      void loadSyncBadge();
    }, 120_000);

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void loadSyncBadge();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      active = false;
      if (timer) window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [settings.management.faciliooEnabled, settings.management.faciliooLiveSyncEnabled]);

  useEffect(() => {
    let active = true;
    const token = getAuthToken();
    if (!token) {
      setTenantTrialStatus(null);
      return;
    }

    const loadTenantTrial = async () => {
      try {
        const trial = await api.getTenantTrialStatus();
        if (!active) return;
        setTenantTrialStatus(trial);
      } catch {
        if (!active) return;
        setTenantTrialStatus(null);
      }
    };

    void loadTenantTrial();

    return () => {
      active = false;
    };
  }, [authUser?.id, tenantSlug]);

  useEffect(() => {
    let active = true;
    let timer: number | null = null;
    const token = getAuthToken();
    if (!token || !authUser || isOwnerArea) return;

    const sendHeartbeat = async () => {
      try {
        await api.adminHeartbeat();
      } catch (error) {
        if (!active) return;
        const message = error instanceof Error ? error.message : "";
        const isAuthError = /\bAPI (401|403)\b/.test(message);
        if (isAuthError) {
          clearAuthToken();
          setAuthUser(null);
          navigate("/admin-login", { replace: true, state: { sessionExpired: true } });
        }
      }
    };

    void sendHeartbeat();
    timer = window.setInterval(() => {
      void sendHeartbeat();
    }, 60_000);

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void sendHeartbeat();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      active = false;
      if (timer) window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [authUser?.id, isOwnerArea, navigate]);

  useEffect(() => {
    if (!settingsHydrated) return;
    if (!getAuthToken()) return;
    const fingerprint = persistedSettingsFingerprint(settings);
    if (lastPersistedSettingsRef.current === fingerprint) return;

    api.saveUiSettings(settings as UiSettingsApi)
      .then((saved) => {
        const next: UiSettings = {
          ...defaultUiSettings,
          ...saved,
          management: { ...defaultUiSettings.management, ...(saved.management ?? {}) },
          mail: { ...defaultUiSettings.mail, ...(saved.mail ?? {}) },
          reminders: { ...defaultUiSettings.reminders, ...(saved.reminders ?? {}) },
          theme: { ...defaultUiSettings.theme, ...(saved.theme ?? {}) },
          ownerTheme: { ...defaultUiSettings.ownerTheme, ...(saved.ownerTheme ?? {}) }
        };
        lastPersistedSettingsRef.current = persistedSettingsFingerprint(next);
        setSettings(next);
        setSettingsSaveStatus({ type: "ok", text: "Einstellungen gespeichert." });
        window.setTimeout(() => setSettingsSaveStatus(null), 2500);
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : "Einstellungen konnten nicht gespeichert werden.";
        setSettingsSaveStatus({ type: "info", text: message });
      });
  }, [settings, settingsHydrated]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--bg", settings.theme.bg);
    root.style.setProperty("--surface", settings.theme.surface);
    root.style.setProperty("--header-bg", settings.theme.headerBg);
    root.style.setProperty("--line", settings.theme.line);
    root.style.setProperty("--text", settings.theme.text);
    root.style.setProperty("--primary", settings.theme.primary);
    root.style.setProperty("--list-header-bg", settings.theme.listHeaderBg);
    root.style.setProperty("--list-alternate-row-bg", settings.theme.listAlternateRowBg);
    root.style.setProperty("--page-gradient-from", settings.theme.gradientFrom);
    root.style.setProperty("--page-gradient-to", settings.theme.gradientTo);
    root.style.setProperty("--page-gradient-enabled", settings.theme.gradientEnabled ? "1" : "0");
    root.style.setProperty("--page-gradient-strength", settings.theme.gradientEnabled ? "100%" : "0%");
    root.style.setProperty("--nav-bg", settings.theme.navBg);
    root.style.setProperty("--nav-text", settings.theme.navText);
    root.style.setProperty("--nav-active", settings.theme.navActive);
    root.style.setProperty("--nav-active-text", settings.theme.navActiveText);
    root.style.setProperty("--nav-border", settings.theme.navBorder);
  }, [settings.theme]);

  useEffect(() => {
    let active = true;
    const token = getAuthToken();
    if (!token) {
      setAuthLoading(false);
      setAuthUser(null);
      setAuthServerError(null);
      return;
    }

    setAuthLoading(true);
    setAuthServerError(null);
    api.adminMe()
      .then((res) => {
        if (!active) return;
        setAuthUser(res.user ?? null);
        setAuthServerError(null);
      })
      .catch((error) => {
        if (!active) return;
        const message = error instanceof Error ? error.message : "";
        const isAuthError = /\bAPI (401|403)\b/.test(message);

        if (isAuthError) {
          clearAuthToken();
          setAuthUser(null);
          setAuthServerError(null);
          navigate("/admin-login", { replace: true, state: { sessionExpired: true } });
          return;
        }

        // Bei 5xx / Netzwerkproblemen Session behalten und Retry anbieten.
        setAuthUser(null);
        setAuthServerError("Server aktuell nicht erreichbar. Bitte erneut versuchen.");
      })
      .finally(() => {
        if (active) setAuthLoading(false);
      });

    return () => {
      active = false;
    };
  }, [authRetryNonce, navigate]);

  const demoTrialBadgeLabel =
    tenantTrialStatus?.enabled && tenantTrialStatus.running
      ? `Demo: noch ${Math.max(0, Number(tenantTrialStatus.remainingDays ?? 0))} Tag${Math.max(0, Number(tenantTrialStatus.remainingDays ?? 0)) === 1 ? "" : "e"}`
      : null;
  const hasPermission = (key: keyof AuthUser["permissions"]) => authUser?.permissions?.[key] !== false;
  const firstAllowedPath = (() => {
    const candidates: Array<{ path: string; key: keyof AuthUser["permissions"] }> = [
      { path: "/", key: "dashboard" },
      { path: "/resolutions", key: "resolutions" },
      { path: "/objects", key: "objects" },
      { path: "/units", key: "units" },
      { path: "/owners", key: "owners" },
      { path: "/assignments", key: "assignments" },
      { path: "/imports", key: "imports" },
      { path: "/templates", key: "templates" },
      { path: "/settings", key: "settings" },
      { path: "/admin-users", key: "admin_users" },
      { path: "/platform-tenants", key: "platform_tenants" },
      { path: "/audit", key: "audit" },
      { path: "/debug", key: "debug" }
    ];
    const first = candidates.find((entry) => hasPermission(entry.key));
    return first?.path ?? "/settings";
  })();
  const showPlatformMenu = hasPermission("platform_tenants") && (isPlatformPortalHost || platformTenantAliases.has(tenantSlug));
  const isFrameworkV2 = settings.management.uiFramework === "mockup_v2";
  const showGlobalFetchLoader = !!authUser && !authLoading && globalFetchLoaderVisible && isFrameworkV2;
  const guardPlatformOnly = <T,>(element: T): T | JSX.Element =>
    isPlatformPortalHost ? <Navigate to="/platform-tenants" replace /> : element;
  const guardPermission = <T,>(permissionKey: keyof AuthUser["permissions"], element: T): T | JSX.Element =>
    hasPermission(permissionKey) ? element : <Navigate to={firstAllowedPath} replace />;

  if (isOwnerArea) {
    return (
      <Routes>
        <Route path="/owner-login" element={<OwnerVotingLoginPage />} />
        <Route path="/owner-vote/:token" element={<OwnerVotingPage />} />
        <Route path="*" element={<Navigate to="/owner-login" replace />} />
      </Routes>
    );
  }

  if (authLoading) {
    if (settings.management.uiFramework === "mockup_v2") {
      return (
        <div className="v2-global-fetch-loader v2-global-fetch-loader-auth" aria-live="polite" aria-label="Seite lädt">
          <div className="v2-global-fetch-loader-ring" aria-hidden="true">
            <span className="v2-global-fetch-loader-dot" style={{ "--i": 0 } as CSSProperties} />
            <span className="v2-global-fetch-loader-dot" style={{ "--i": 1 } as CSSProperties} />
            <span className="v2-global-fetch-loader-dot" style={{ "--i": 2 } as CSSProperties} />
            <span className="v2-global-fetch-loader-dot" style={{ "--i": 3 } as CSSProperties} />
            <span className="v2-global-fetch-loader-dot" style={{ "--i": 4 } as CSSProperties} />
            <span className="v2-global-fetch-loader-dot" style={{ "--i": 5 } as CSSProperties} />
            <span className="v2-global-fetch-loader-dot" style={{ "--i": 6 } as CSSProperties} />
            <span className="v2-global-fetch-loader-dot" style={{ "--i": 7 } as CSSProperties} />
          </div>
        </div>
      );
    }
    return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>Lade Anmeldung...</div>;
  }

  if (!authUser) {
    if (getAuthToken()) {
      return (
        <div className="backend-check-page" style={{ "--backend-check-bg": `url(${backendCheckBg})` } as CSSProperties}>
          <section className="backend-check-card" aria-live="polite">
            <div className="backend-check-icon" aria-hidden="true">
              <svg viewBox="0 0 64 64" fill="none">
                <path d="M21.5 45.5H20a11 11 0 1 1 2.6-21.7 14.5 14.5 0 0 1 27.9 5.5A8.8 8.8 0 0 1 49 46h-3.5" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M32 27v12" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                <circle cx="32" cy="48" r="2.6" fill="currentColor" />
              </svg>
            </div>
            <h3>Verbindung zum Backend prüfen</h3>
            <p>
              <span>Der Server ist derzeit nicht erreichbar.</span>
              <span>Bitte versuchen Sie es in wenigen Momenten erneut.</span>
            </p>
            <div className="backend-check-divider" aria-hidden="true">
              <span />
            </div>
            <div className="backend-check-actions">
              <button type="button" className="backend-check-primary" onClick={() => setAuthRetryNonce((v) => v + 1)}>
                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M15.4 7.2A5.8 5.8 0 1 0 16 10M15.4 4.5v2.7h-2.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Erneut versuchen
              </button>
              <button
                type="button"
                className="backend-check-secondary"
                onClick={() => {
                  clearAuthToken();
                  setAuthServerError(null);
                  setAuthUser(null);
                  navigate("/admin-login", { replace: true });
                }}
              >
                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M8 4.5H5.8A1.8 1.8 0 0 0 4 6.3v7.4a1.8 1.8 0 0 0 1.8 1.8H8M12 6.5l3.5 3.5-3.5 3.5M15 10H8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Abmelden
              </button>
            </div>
          </section>
          <p className="backend-check-support">
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M4 11.5V9.7a6 6 0 0 1 12 0v1.8M4 11.5h2.4v4H4a1 1 0 0 1-1-1v-2a1 1 0 0 1 1-1ZM16 11.5h-2.4v4H16a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1ZM13.6 15.5c-.6 1-1.7 1.5-3.6 1.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Bei anhaltenden Problemen kontaktieren Sie bitte den <a href="mailto:support@derumlaufbeschluss.de">Support</a>.</span>
          </p>
        </div>
      );
    }

    return (
      <Routes>
        <Route path="/" element={isLandingHost ? <LandingPage /> : <Navigate to="/admin-login" replace />} />
        <Route path="/landing" element={isLandingHost ? <LandingPage /> : <Navigate to="/admin-login" replace />} />
        <Route path="/impressum" element={isLandingHost ? <ImpressumPage /> : <Navigate to="/admin-login" replace />} />
        <Route path="/datenschutz" element={isLandingHost ? <DatenschutzPage /> : <Navigate to="/admin-login" replace />} />
        <Route path="/admin-login" element={<AdminLoginPage onAuthenticated={setAuthUser} />} />
        <Route
          path="*"
          element={
            <Navigate
              to={location.pathname.startsWith("/admin-login") || !isLandingHost ? "/admin-login" : "/"}
              state={{ from: location.pathname }}
              replace
            />
          }
        />
      </Routes>
    );
  }

  const handleLogout = async () => {
    try {
      await api.adminLogout();
    } catch {
      // ignore
    }
    clearAuthToken();
    setAuthUser(null);
    navigate("/admin-login", { replace: true });
  };

  return (
    <div className="app-shell">
      <Sidebar
        mode="desktop"
        navBg={settings.theme.navBg}
        frameworkMode={settings.management.uiFramework}
        authUser={authUser}
        onLogout={handleLogout}
        showPlatformMenu={showPlatformMenu}
        platformOnlyMode={isPlatformPortalHost}
        permissions={authUser.permissions}
      />
      {mobileNavOpen ? (
        <div className="mobile-nav-overlay open" onClick={() => setMobileNavOpen(false)}>
          <div className="mobile-nav-panel" onClick={(e) => e.stopPropagation()}>
              <Sidebar
                mode="mobile"
                navBg={settings.theme.navBg}
                frameworkMode={settings.management.uiFramework}
                authUser={authUser}
                onLogout={handleLogout}
                showPlatformMenu={showPlatformMenu}
                platformOnlyMode={isPlatformPortalHost}
                permissions={authUser.permissions}
                onNavigate={() => setMobileNavOpen(false)}
                onClose={() => setMobileNavOpen(false)}
              />
          </div>
        </div>
      ) : null}
      <div className="main-area">
        {showGlobalFetchLoader ? (
          <div className="v2-global-fetch-loader" aria-live="polite" aria-label="Seite lädt">
            <div className="v2-global-fetch-loader-ring" aria-hidden="true">
              <span className="v2-global-fetch-loader-dot" style={{ "--i": 0 } as CSSProperties} />
              <span className="v2-global-fetch-loader-dot" style={{ "--i": 1 } as CSSProperties} />
              <span className="v2-global-fetch-loader-dot" style={{ "--i": 2 } as CSSProperties} />
              <span className="v2-global-fetch-loader-dot" style={{ "--i": 3 } as CSSProperties} />
              <span className="v2-global-fetch-loader-dot" style={{ "--i": 4 } as CSSProperties} />
              <span className="v2-global-fetch-loader-dot" style={{ "--i": 5 } as CSSProperties} />
              <span className="v2-global-fetch-loader-dot" style={{ "--i": 6 } as CSSProperties} />
              <span className="v2-global-fetch-loader-dot" style={{ "--i": 7 } as CSSProperties} />
            </div>
          </div>
        ) : null}
        <Topbar
          settings={settings}
          authUser={authUser}
          faciliooSyncBadge={
            isPlatformPortalHost
              ? null
              : settings.management.faciliooEnabled !== false && settings.management.faciliooLiveSyncEnabled
                ? faciliooSyncBadge
                : null
          }
          demoTrialBadgeLabel={demoTrialBadgeLabel}
          onLogout={handleLogout}
          onOpenMenu={() => setMobileNavOpen(true)}
        />
        <main className="content">
          <Routes>
            <Route path="/" element={hasPermission("dashboard") ? (isPlatformPortalHost ? <Navigate to="/platform-tenants" replace /> : <DashboardPage authUser={authUser} />) : <Navigate to={firstAllowedPath} replace />} />
            <Route
              path="/objects"
              element={guardPermission(
                "objects",
                guardPlatformOnly(
                  <ObjectsPage
                    canCreate={hasPermission("objects_create")}
                    canUpdate={hasPermission("objects_update")}
                    canDelete={hasPermission("objects_delete")}
                  />
                )
              )}
            />
            <Route
              path="/objects/:id"
              element={
                guardPermission(
                  "objects",
                  guardPlatformOnly(
                  <ObjectDetailPage
                    canManageMasterDataActions={
                      hasPermission("objects_create") ||
                      hasPermission("objects_update") ||
                      hasPermission("objects_delete") ||
                      hasPermission("units_create") ||
                      hasPermission("units_update") ||
                      hasPermission("units_delete") ||
                      hasPermission("owners_create") ||
                      hasPermission("owners_update") ||
                      hasPermission("owners_delete") ||
                      hasPermission("assignments_create") ||
                      hasPermission("assignments_update") ||
                      hasPermission("assignments_delete") ||
                      hasPermission("resolutions_update") ||
                      hasPermission("resolutions_delete")
                    }
                    canCreateResolutions={hasPermission("resolutions_create")}
                  />
                  )
                )
              }
            />
            <Route
              path="/units"
              element={guardPermission(
                "units",
                guardPlatformOnly(
                  <UnitsPage
                    canCreate={hasPermission("units_create")}
                    canUpdate={hasPermission("units_update")}
                    canDelete={hasPermission("units_delete")}
                  />
                )
              )}
            />
            <Route
              path="/owners"
              element={guardPermission(
                "owners",
                guardPlatformOnly(
                  <OwnersPage
                    canCreate={hasPermission("owners_create")}
                    canUpdate={hasPermission("owners_update")}
                    canDelete={hasPermission("owners_delete")}
                  />
                )
              )}
            />
            <Route
              path="/assignments"
              element={guardPermission(
                "assignments",
                guardPlatformOnly(
                  <AssignmentsPage
                    canCreate={hasPermission("assignments_create")}
                    canUpdate={hasPermission("assignments_update")}
                    canDelete={hasPermission("assignments_delete")}
                  />
                )
              )}
            />
            <Route path="/imports" element={guardPermission("imports", guardPlatformOnly(<ImportsPage />))} />
            <Route
              path="/resolutions"
              element={guardPermission(
                "resolutions",
                guardPlatformOnly(
                  <ResolutionsPage
                    canManageMasterDataActions={
                      hasPermission("resolutions_create") ||
                      hasPermission("resolutions_update") ||
                      hasPermission("resolutions_delete")
                    }
                    canCreateResolutions={hasPermission("resolutions_create")}
                    canEditResolutions={hasPermission("resolutions_update")}
                    canDeleteResolutions={hasPermission("resolutions_delete")}
                    canDeleteCompletedResolutions={hasPermission("resolutions_delete_completed")}
                  />
                )
              )}
            />
            <Route
              path="/resolutions/:id"
              element={guardPermission(
                "resolutions",
                guardPlatformOnly(
                  <ResolutionDetailPage
                    canManageMasterDataActions={hasPermission("resolutions_update") || hasPermission("resolutions_delete")}
                  />
                )
              )}
            />
            <Route path="/analog-scan" element={guardPermission("qr_scan", guardPlatformOnly(<AnalogScanPage />))} />
            <Route path="/analog-scan/:token" element={guardPermission("qr_scan", guardPlatformOnly(<AnalogScanPage />))} />
            <Route path="/audit" element={guardPermission("audit", guardPlatformOnly(<LogsPage />))} />
            <Route path="/debug" element={guardPermission("debug", guardPlatformOnly(<AuditPage />))} />
            <Route path="/templates" element={guardPermission("templates", guardPlatformOnly(<TemplatesPage />))} />
            <Route
              path="/search"
              element={
                guardPlatformOnly(
                  hasPermission("resolutions") || hasPermission("objects") || hasPermission("units") || hasPermission("owners")
                    ? <GlobalSearchPage />
                    : <Navigate to={firstAllowedPath} replace />
                )
              }
            />
            <Route
              path="/settings"
              element={
                isPlatformPortalHost && !showPlatformMenu ? (
                  <Navigate to="/admin-login" replace />
                ) : (
                  guardPermission(
                    "settings",
                    <SettingsPage settings={settings} onChange={setSettings} authUser={authUser} saveStatus={settingsSaveStatus} />
                  )
                )
              }
            />
            <Route
              path="/admin-users"
              element={
                isPlatformPortalHost && !showPlatformMenu
                  ? <Navigate to="/admin-login" replace />
                  : guardPermission("admin_users", <AdminUsersPage authUser={authUser} />)
              }
            />
            <Route
              path="/platform-tenants"
              element={
                showPlatformMenu ? (
                  guardPermission("platform_tenants", <PlatformTenantsPage />)
                ) : (
                  <Navigate to={isPlatformPortalHost ? "/settings" : "/"} replace />
                )
              }
            />
            <Route path="/landing" element={<Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};


